import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { User } from 'firebase/auth';
import styles from '@/styles/modules/DropDownMenu.module.css';
import Image from 'next/image';
import HStack from './HStack'; // Add this import

interface DropdownMenuProps {
    currentUser: User | null;
    onSignOut: () => void;
    onChatClick?: () => void; // Add this new optional prop
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ currentUser, onSignOut, onChatClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showInfoSubmenu, setShowInfoSubmenu] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setShowInfoSubmenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleItemClick = () => {
        setIsOpen(false);
        setShowInfoSubmenu(false);
    };

    const handleChatClick = () => {
        if (onChatClick) {
            onChatClick();
        }
    };

    const toggleInfoSubmenu = () => {
        console.log('Before toggle:', showInfoSubmenu);
        console.log('CSS Module classes:', {
            submenuChevron: styles.submenuChevron,
            rotated: styles.rotated
        });
        setShowInfoSubmenu(!showInfoSubmenu);
        console.log('After toggle:', !showInfoSubmenu);
    };


    return (
        <HStack spacing={12} alignment="center" className={styles.dropdownContainer}>
            <Link href="/chat" className={styles.chatButton} onClick={handleChatClick}>
                <svg className={styles.chatIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Link>

            <div className={styles.dropdown} ref={dropdownRef}>
                <button
                    className={styles.dropdownButton}
                    onClick={() => setIsOpen(!isOpen)}
                    onMouseEnter={() => setIsOpen(true)}
                >
                    <div className={styles.userAvatar}>
                        {currentUser?.photoURL ? (
                            <Image
                                src={currentUser.photoURL}
                                alt="User Avatar"
                                width={24}
                                height={24}
                                className={styles.avatarImage}
                            />
                        ) : (
                            <span>{currentUser?.email?.[0]?.toUpperCase() || 'U'}</span>
                        )}
                    </div>
                    <svg className={`${styles.chevron} ${isOpen ? styles.open : ''}`} width="16" height="16" viewBox="0 0 16 16">
                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {isOpen && (
                    <div className={styles.dropdownMenu}>
                        <div className={styles.userInfo}>
                            <div className={styles.userName}>{currentUser?.displayName || 'User'}</div>
                            <div className={styles.userEmail}>{currentUser?.email}</div>
                        </div>

                        <div className={styles.menuDivider}></div>

                        <Link href="/pricing" className={styles.menuItem} onClick={handleItemClick}>
                            <span className={styles.icon}>⭐</span>
                            Upgrade Plan
                        </Link>

                        <button
                            className={`${styles.menuItem} ${styles.menuItemWithSubmenu}`}
                            onClick={toggleInfoSubmenu}
                        >
                            <span className={styles.icon}>ℹ️</span>
                            Info
                            <svg
                                className={`${styles.submenuChevron} ${showInfoSubmenu ? styles.rotated : ''}`}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                style={{
                                    transformOrigin: 'center',
                                    transform: showInfoSubmenu ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease'
                                }}
                            >
                                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {showInfoSubmenu && (
                            <div className={styles.submenuInline}>
                                <Link href="/docs" className={styles.submenuItem} onClick={handleItemClick}>
                                    <span className={styles.submenuIcon}>📚 </span>
                                    Docs
                                </Link>
                                <Link href="/about" className={styles.submenuItem} onClick={handleItemClick}>
                                    <span className={styles.submenuIcon}>ℹ️ </span>
                                    About
                                </Link>
                                <Link href="/conversations" className={styles.submenuItem} onClick={handleItemClick}>
                                    <span className={styles.submenuIcon}>💬 </span>
                                    Chat History
                                </Link>
                                
                                {/* <Link href="mailto:support@lvl3.ai" className={styles.submenuItem} onClick={handleItemClick}>
                                    <span className={styles.submenuIcon}>🎧</span>
                                    Support
                                </Link> */}
                            </div>
                        )}

                        <Link href="/settings" className={styles.menuItem} onClick={handleItemClick}>
                            <span className={styles.icon}>⚙️</span>
                            Settings
                        </Link>

                        <div className={styles.menuDivider}></div>

                        <button className={`${styles.menuItem} ${styles.logoutItem}`} onClick={onSignOut}>
                            <span className={styles.icon}>👋</span>
                            Log Out
                        </button>
                    </div>
                )}
            </div>
        </HStack>
    );
};

export default DropdownMenu;